import {
	ApiPlatformEnum,
	EcommerceCartDataSourceEnum,
	EcommerceShoppingListDataSourceEnum
} from '@inter-ikea-kompis/enums';
import type { Exception, ServerErrorResponseException } from '@inter-ikea-kompis/exceptions';
import { ExceptionTypeEnum, ServiceException } from '@inter-ikea-kompis/exceptions';
import type {
	IKompisTranslations,
	IMvEcomToken,
	IShoppingItem,
	IShoppingList
} from '@inter-ikea-kompis/types';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';

import ChinaAuthenticationService from '../../services/authentication/china/ChinaAuthenticationService.js';
import ChinaShoppingListService from '../../services/shopping/china/shopping-list/ChinaShoppingListService.js';
import IksaShoppingBagService from '../../services/shopping/iksa/IksaShoppingBagService.js';
import IngkaShoppingListService from '../../services/shopping/ingka/shopping-list/IngkaShoppingListService.js';
import IowsShoppingBagService from '../../services/shopping/iows/IowsShoppingBagService.js';
import MvEcomShoppingBagService from '../../services/shopping/mvecom/MvEcomShoppingBagService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import LocalizationService from '../localization/LocalizationService.js';
import AuthenticationQueue from './AuthenticationQueue.js';
import type IShoppingListServiceOptions from './IShoppingListServiceOptions.js';

/**
 * This class is used as a simple interface for shopping services.
 */
export default class ShoppingListService {
	private readonly consumer: string;
	private readonly contract: string;
	private readonly localizationPrefixApplication?: string;
	private readonly localizationEndpoints?: string[];
	private readonly localizationOverrides?: object;
	private readonly locale: string;
	private readonly shoppingListName?: string;
	private readonly cache: CacheStore;
	private readonly dexfConfiguration: DexfConfiguration;
	private readonly translations?: IKompisTranslations;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 */
	constructor(options: IShoppingListServiceOptions) {
		this.consumer = options.consumer;
		this.contract = options.contract;
		this.localizationPrefixApplication = options.localizationPrefixApplication;
		this.localizationEndpoints = options.localizationEndpoints;
		this.localizationOverrides = options.localizationOverrides;
		this.locale = options.locale;
		this.shoppingListName = options.shoppingListName;
		this.cache = options.cache || new GlobalCacheStore();
		this.dexfConfiguration = new DexfConfiguration(options);
		this.translations = options.translations;
	}

	/**
	 * Adds items to shopping cart in IRW.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @param options.designCode VPC code used to group articles in the shopping list.
	 * @param options.singleItems Single items to be added individually outside the group. Only applicable when a designCode has been set.
	 * @returns Promise object.
	 */
	public async addToList(options: {
		items: IShoppingItem[];
		designCode?: string;
		singleItems?: IShoppingItem[];
	}): Promise<IShoppingList> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const eCommerceCartDataSource = kompis.serviceSettings.eCommerceCartDataSource;
		const eCommerceShoppingListDataSource = kompis.serviceSettings.eCommerceShoppingListDataSource;
		const apiPlatform = kompis.serviceSettings.apiPlatform;

		switch (eCommerceShoppingListDataSource) {
			case EcommerceShoppingListDataSourceEnum.shoppingListApi:
				return await this.addToListOneWeb({
					items: options.items,
					designCode: options.designCode,
					singleItems: options.singleItems
				});
			case EcommerceShoppingListDataSourceEnum.chinaShoppingListApi:
				return await this.addToListChina({
					items: options.items
				});
			case EcommerceShoppingListDataSourceEnum.iows:
				return await this.addToListIows({
					items: options.items
				});
			case EcommerceShoppingListDataSourceEnum.mvecom:
				return await this.addToListMvEcom({
					items: options.items
				});
			case EcommerceShoppingListDataSourceEnum.mvecomV2:
				return await this.addToListMvEcom({
					items: options.items,
					useBearerToken: true
				});
			case EcommerceShoppingListDataSourceEnum.iksa:
				return await this.addToListIksa({
					items: options.items
				});
			case EcommerceShoppingListDataSourceEnum.default:
				switch (eCommerceCartDataSource) {
					case EcommerceCartDataSourceEnum.iows:
					case EcommerceCartDataSourceEnum.cartApi:
						return await this.addToListIows({
							items: options.items
						});
					case EcommerceCartDataSourceEnum.default:
						switch (apiPlatform) {
							case ApiPlatformEnum.irw:
								return await this.addToListIows({
									items: options.items
								});
							default:
								throw new ServiceException(
									`Failed to add items to shopping list. Unknown api platform "${apiPlatform}".`
								);
						}
					default:
						throw new ServiceException(
							`Failed to add items to shopping list. Unknown shopping cart data source "${eCommerceCartDataSource}".`
						);
				}
			default:
				throw new ServiceException(
					`Failed to add items to shopping list. Unknown shopping list data source "${eCommerceShoppingListDataSource}".`
				);
		}
	}

	/**
	 * Returns total amount of items in a shopping list.
	 *
	 * @returns Promise object.
	 */
	public async getListCount(): Promise<IShoppingList['listCount']> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const eCommerceCartDataSource = kompis.serviceSettings.eCommerceCartDataSource;
		const eCommerceShoppingListDataSource = kompis.serviceSettings.eCommerceShoppingListDataSource;
		const apiPlatform = kompis.serviceSettings.apiPlatform;

		switch (eCommerceShoppingListDataSource) {
			case EcommerceShoppingListDataSourceEnum.shoppingListApi:
				return await this.getListCountOneWeb();
			case EcommerceShoppingListDataSourceEnum.iows:
				return await this.getListCountIows();
			case EcommerceShoppingListDataSourceEnum.mvecom:
				return await this.getListCountMvEcom();
			case EcommerceShoppingListDataSourceEnum.mvecomV2:
				return await this.getListCountMvEcom({ useBearerToken: true });
			case EcommerceShoppingListDataSourceEnum.chinaShoppingListApi:
				return await this.getListCountChina();
			case EcommerceShoppingListDataSourceEnum.iksa:
				return await this.getListCountIksa();
			case EcommerceShoppingListDataSourceEnum.default:
				switch (eCommerceCartDataSource) {
					case EcommerceCartDataSourceEnum.iows:
					case EcommerceCartDataSourceEnum.cartApi:
						return await this.getListCountIows();
					case EcommerceCartDataSourceEnum.default:
						switch (apiPlatform) {
							case ApiPlatformEnum.irw:
								return await this.getListCountIows();
							default:
								throw new ServiceException(
									`Failed to get list count. Unknown api platform "${apiPlatform}".`
								);
						}
					default:
						throw new ServiceException(
							`Failed to get list count. Unknown shopping cart data source "${eCommerceCartDataSource}".`
						);
				}
			default:
				throw new ServiceException(
					`Failed to get list count. Unknown shopping list data source "${eCommerceShoppingListDataSource}".`
				);
		}
	}

	/**
	 * Returns shopping list name.
	 */
	private async getShoppingListName(): Promise<string> {
		if (this.shoppingListName) {
			return this.shoppingListName;
		} else if (this.translations) {
			return this.translations.plannerShoppingListName;
		} else if (this.localizationEndpoints && this.locale) {
			const localizationService = new LocalizationService({
				localizationPrefixApplication: this.localizationPrefixApplication,
				localizationEndpoints: this.localizationEndpoints,
				localizationOverrides: this.localizationOverrides,
				locale: this.locale,
				cache: this.cache
			});
			const localizedInformation = await localizationService.getLocalizedInformation();

			return localizedInformation.translations.plannerShoppingListName;
		}

		throw new Error(
			'Shopping list name must be defined either through the "shoppingListName", "translations" option or "localizedInformation" props.'
		);
	}

	/**
	 * Adds items to shopping cart.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @returns Promise object.
	 */
	private async addToListIows(options: { items: IShoppingItem[] }): Promise<IShoppingList> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new IowsShoppingBagService({
			consumer: this.consumer,
			contract: this.contract,
			endpoint: kompis.endpoints.iowsShoppingBagService
		});
		const userId = await this.getIowsUserId();
		const shoppingList = await shoppingService.addToList({
			items: options.items,
			userId,
			shoppingListName: await this.getShoppingListName()
		});

		return shoppingList;
	}

	/**
	 * Returns list count for IOWS.
	 *
	 * @returns Promise object.
	 */
	private async getListCountIows(): Promise<IShoppingList['listCount']> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new IowsShoppingBagService({
			consumer: this.consumer,
			contract: this.contract,
			endpoint: kompis.endpoints.iowsShoppingBagService
		});
		const userId = await this.getIowsUserId();
		return await shoppingService.getListCount({
			userId,
			shoppingListName: await this.getShoppingListName()
		});
	}

	/**
	 * Adds items to shopping list for MvEcom.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @param options.useBearerToken If bearer token should be used for user authentication.
	 * @returns Promise object.
	 */
	private async addToListMvEcom(options: {
		items: IShoppingItem[];
		useBearerToken?: boolean;
	}): Promise<IShoppingList> {
		const serviceOptions = await this.getMvEcomService(options.useBearerToken);
		const shoppingList = await serviceOptions.service.addToList({
			items: options.items,
			userId: serviceOptions.userId,
			shoppingListName: await this.getShoppingListName()
		});

		return shoppingList;
	}

	/**
	 * Returns list count for MvEcom.
	 *
	 * @returns Promise object.
	 */
	private async getListCountMvEcom(options?: {
		useBearerToken: boolean;
	}): Promise<IShoppingList['listCount']> {
		const serviceOptions = await this.getMvEcomService(options?.useBearerToken);
		return await serviceOptions.service.getListCount({
			userId: serviceOptions.userId,
			shoppingListName: await this.getShoppingListName()
		});
	}

	/**
	 * Returns authenticated MvEcom service and user id.
	 *
	 * @param useBearerToken If bearer token should be used for user authentication.
	 * @returns Promise object.
	 */
	private async getMvEcomService(
		useBearerToken: boolean | undefined
	): Promise<{ service: MvEcomShoppingBagService; userId: string }> {
		const { kompis } = await this.dexfConfiguration.getSettings();

		let bearerToken: string | undefined;
		let userId: string;
		if (useBearerToken) {
			const token = await this.getMvEcomToken(kompis.endpoints.mvEcomAuthenticationService);
			if (token) {
				bearerToken = token.value;
				userId = token.customerId;
			} else {
				userId = await this.getIowsUserId();
			}
		} else {
			userId = await this.getIowsUserId();
		}

		const shoppingService = new MvEcomShoppingBagService({
			mvEcomApiKey: kompis.serviceSettings.mvEcomApiKey,
			endpoint: kompis.endpoints.mvEcomShoppingBagService,
			bearerToken
		});

		return { service: shoppingService, userId };
	}

	/**
	 * Adds items to shopping list.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @param options.designCode ...
	 * @param options.singleItems ...
	 * @returns Promise object.
	 */
	private async addToListOneWeb(options: {
		items: IShoppingItem[];
		designCode?: string;
		singleItems?: IShoppingItem[];
	}): Promise<IShoppingList> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new IngkaShoppingListService({
			endpoint: kompis.endpoints.ingkaShoppingListService,
			clientId: kompis.serviceSettings.ingkaShoppingListClientId,
			clientSecret: kompis.serviceSettings.ingkaShoppingListClientSecret,
			itemGroupingInBagEnabled: kompis.serviceSettings.itemGroupingInBagEnabled
		});
		const authQueue = new AuthenticationQueue(this.cache);
		const token = await authQueue.getAuthTokenOneWeb({
			endpoint: kompis.endpoints.oneWebAuthenticationToken
		});

		if (!token) {
			throw new ServiceException('Token is not valid.');
		}

		const shoppingList = await shoppingService.addToList({
			items: options.items,
			token,
			shoppingListName: await this.getShoppingListName(),
			designCode: options.designCode,
			singleItems: options.singleItems
		});

		return shoppingList;
	}

	/**
	 * Returns list count for One Web.
	 *
	 * @returns Promise object.
	 */
	private async getListCountOneWeb(): Promise<number> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new IngkaShoppingListService({
			endpoint: kompis.endpoints.ingkaShoppingListService,
			clientId: kompis.serviceSettings.ingkaShoppingListClientId,
			clientSecret: kompis.serviceSettings.ingkaShoppingListClientSecret,
			itemGroupingInBagEnabled: kompis.serviceSettings.itemGroupingInBagEnabled
		});
		const authQueue = new AuthenticationQueue(this.cache);
		const token = await authQueue.getAuthTokenOneWeb({
			endpoint: kompis.endpoints.oneWebAuthenticationToken
		});

		if (!token) {
			throw new ServiceException('Token is not valid.');
		}

		return await shoppingService.getListCount({
			token,
			shoppingListName: await this.getShoppingListName()
		});
	}

	/**
	 * Adds items to shopping list.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @returns Promise object.
	 */
	private async addToListChina(options: { items: IShoppingItem[] }): Promise<IShoppingList> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new ChinaShoppingListService({
			endpoint: kompis.endpoints.chinaShoppingListService
		});
		const authQueue = new AuthenticationQueue(this.cache);
		const token = await authQueue.getAuthTokenChina({
			refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
			loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
		});

		if (!token) {
			throw new ServiceException('Token is not valid.');
		}

		try {
			return await shoppingService.addToList({
				items: options.items,
				token
			});
		} catch (error) {
			if (
				(<Exception>error).exceptionType === ExceptionTypeEnum.serverErrorResponse &&
				(<ServerErrorResponseException>error).serverResponse.status === 401
			) {
				const token = await authQueue.getAuthTokenChina({
					refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
					loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage,
					login: true
				});

				if (!token) {
					throw new ServiceException('Token is not valid.');
				}

				return await shoppingService.addToList({
					items: options.items,
					token
				});
			}
			throw error;
		}
	}

	/**
	 * Returns list count for China.
	 *
	 * @returns Promise object.
	 */
	private async getListCountChina(): Promise<number> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		if (
			!new ChinaAuthenticationService({
				refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
				loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
			}).hasCookie()
		) {
			return 0;
		}

		const shoppingService = new ChinaShoppingListService({
			endpoint: kompis.endpoints.chinaShoppingListService
		});
		const authQueue = new AuthenticationQueue(this.cache);
		const token = await authQueue.getAuthTokenChina({
			refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
			loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage
		});

		if (!token) {
			throw new ServiceException('Token is not valid.');
		}

		try {
			return await shoppingService.getListCount({
				token
			});
		} catch (error) {
			if (
				(<Exception>error).exceptionType === ExceptionTypeEnum.serverErrorResponse &&
				(<ServerErrorResponseException>error).serverResponse.status === 401
			) {
				const token = await authQueue.getAuthTokenChina({
					refreshTokenEndpoint: kompis.endpoints.chinaAuthenticationRefreshToken,
					loginPageEndpoint: kompis.endpoints.chinaAuthenticationLoginPage,
					login: true
				});

				if (!token) {
					throw new ServiceException('Token is not valid.');
				}

				return await shoppingService.getListCount({
					token
				});
			}
			throw error;
		}
	}

	/**
	 * Adds items to shopping cart.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @returns Promise object.
	 */
	private async addToListIksa(options: { items: IShoppingItem[] }): Promise<IShoppingList> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new IksaShoppingBagService({
			consumer: this.consumer,
			contract: this.contract,
			endpoint: kompis.endpoints.iksaShoppingBagService
		});
		const userId = await this.getIowsUserId();
		const shoppingList = await shoppingService.addToList({
			items: options.items,
			userId,
			shoppingListName: await this.getShoppingListName()
		});

		return shoppingList;
	}

	/**
	 * Returns list count for IKSA.
	 *
	 * @returns Promise object.
	 */
	private async getListCountIksa(): Promise<IShoppingList['listCount']> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const shoppingService = new IksaShoppingBagService({
			consumer: this.consumer,
			contract: this.contract,
			endpoint: kompis.endpoints.iksaShoppingBagService
		});

		const userId = await this.getIowsUserId();

		return await shoppingService.getListCount({
			userId,
			shoppingListName: await this.getShoppingListName()
		});
	}

	/**
	 * Returns authenticated IOWS user id.
	 *
	 * @returns Promise of user id as string.
	 */
	private async getIowsUserId(): Promise<string> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const authQueue = new AuthenticationQueue(this.cache);
		const user = await authQueue.getAuthenticationIows({
			consumer: this.consumer,
			contract: this.contract,
			endpoint: kompis.endpoints.iowsAuthenticationServiceV2
		});

		return user.id;
	}

	private async getMvEcomToken(endpoint: string): Promise<IMvEcomToken | null> {
		const authQueue = new AuthenticationQueue(this.cache);
		return await authQueue.getAuthTokenMvEcom({
			endpoint: endpoint,
			locale: this.locale
		});
	}
}
